import { Room, Patient } from '../monitor.types';

export interface MonitorProps {
  operationRooms?: Room[];
  waitingRoom?: Room;
  preOp?: Room;
  pacu?: Room;
  postOps?: Room[];
  OR?: Room;
  postOp?: Room;
}

export const patientCounters = {
  /**
   * Count total patients across all rooms
   */
  patientNumberFromProps: (props: MonitorProps): number => {
    const { operationRooms = [], waitingRoom, preOp, pacu, postOp } = props;
    return sum([
      (waitingRoom?.patients || []).length,
      (preOp?.patients || []).length,
      (pacu?.patients || []).length,
      (postOp?.patients || []).length,
      sum(operationRooms.map(or => (or.patients || []).length)),
    ]);
  },

  /**
   * Count operation rooms with 'Closing' status
   */
  closingProps: (props: MonitorProps): number => {
    const { operationRooms = [] } = props;
    return operationRooms.filter(or => or.status === 'Closing').length;
  },

  /**
   * Count operation rooms with 'ProcedureEnd' status
   */
  procedureEndProps: (props: MonitorProps): number => {
    const { operationRooms = [] } = props;
    return operationRooms.filter(or => or.status === 'ProcedureEnd').length;
  },

  /**
   * Generic function to count ready patients in a specific room type
   */
  patientRoomNumberFromProps:
    (type: keyof MonitorProps) =>
    (props: MonitorProps): number => {
      const value = props[type];

      if (!value) {
        return 0;
      }

      return (Array.isArray(value) ? value : [value])
        .map(v => {
          const patients = v.patients;

          if (Array.isArray(patients)) {
            return patients.filter((_: Patient) => _.ready).length;
          }

          return 0;
        })
        .reduce((acc, curr) => acc + curr, 0);
    },

  /**
   * Count patients ready for surgeon in preOp
   */
  readyForSurgeonNumberFromProps: (props: MonitorProps): number =>
    countPatientsWhere(props.preOp?.patients, patient => !!patient.readyForSurgeon),

  /**
   * Count patients with hold procedure in preOp
   */
  holdProcedureNumberFromProps: (props: MonitorProps): number =>
    countPatientsWhere(props.preOp?.patients, patient => !!patient.isHoldProcedure),

  /**
   * Count patients with block nerve in preOp
   */
  blockNerveNumberFromProps: (props: MonitorProps): number =>
    countPatientsWhere(props.preOp?.patients, patient => !!patient.isBlockNerve),

  /**
   * Count patients with block nerve in postOps and PACU
   */
  blockNerveNumberFromProps2: (props: MonitorProps): number => {
    const postOps = (props.postOps || []).flatMap(postOp => postOp.patients || []);
    const pacu = props.pacu?.patients || [];

    return [...postOps, ...pacu].filter((_: Patient) => Boolean(_.isBlockNerve)).length;
  },

  /**
   * Count patients needing help in OR
   */
  // FIXME: patient.isHelp doesn't seen to be available un upstream data
  helpNumberFromProps: (props: MonitorProps): number =>
    countPatientsWhere(props.OR?.patients, patient => !!patient.isHelp),

  /**
   * Count patients ready for OR in preOp
   */
  readyForORNumberFromProps: (props: MonitorProps): number =>
    countPatientsWhere(props.preOp?.patients, patient => !!patient.readyForOr),

  /**
   * Count family ready patients in postOps and PACU
   */
  familyHereNumberFromProps: (props: MonitorProps): number => {
    const postOps = (props.postOps || []).flatMap(postOp => postOp.patients || []);
    const pacu = props.pacu?.patients || [];

    return [...postOps, ...pacu].filter(
      // FIXME: patient.familyReady doesn't seen to be available un upstream data
      (patient: Patient) =>
        Boolean(patient.familyReady) || Boolean(patient.familyReadyPACU) || Boolean(patient.familyReadyPOSTOP)
    ).length;
  },

  /**
   * Count patients ready to pickup in postOps and PACU
   */
  readyToPickupNumberFromProps: (props: MonitorProps): number => {
    const postOps = (props.postOps || []).flatMap(postOp => postOp.patients || []);
    const pacu = props.pacu?.patients || [];

    return [...postOps, ...pacu]?.filter((_: Patient) => !!_.caretakerMessages).length;
  },
};

const sum = (arr: number[]): number => arr.reduce((l, r) => l + r, 0);

const countPatientsWhere = (patients: Patient[] | undefined, predicate: (patient: Patient) => boolean): number => {
  if (!Array.isArray(patients)) {
    return 0;
  }
  return patients.filter(predicate).length;
};

// Create specific counter functions for common use cases
export const waitingNumberFromProps = patientCounters.patientRoomNumberFromProps('waitingRoom');
export const pacuNumberFromProps = patientCounters.patientRoomNumberFromProps('pacu');
export const postOpsNumberFromProps = patientCounters.patientRoomNumberFromProps('postOps');
